<template>
    <ModalComponent
        :name="'renovacion-sga-modal'"
        :title="$t('perfil_sga.renovacion_titulo')"
        @close="closeModal"
    >
        <!-- Modal Body (default slot) -->
        <!-- Error state -->
        <b-alert v-if="error" variant="danger" show>
            {{ error }}
        </b-alert>

        <!-- BLOCKED STATE: renewal disabled for this abonado (status.value === 2) -->
        <div v-if="status?.value === 2" class="tw:text-center tw:py-8 tw:px-4">
            <div class="mb-3 d-inline-flex align-items-center justify-content-center"
                 style="width: 64px; height: 64px; border-radius: 50%; background-color: #f1f3f5;"
            >
                <b-icon icon="lock-fill" font-scale="2.4" style="color: #adb5bd;" />
            </div>
            <h5 class="mb-2">{{ $t('perfil_sga.renovacion_bloqueada_titulo') }}</h5>
            <p class="text-muted mb-0 px-4">
                {{ $t('perfil_sga.renovacion_bloqueada_texto') }}
            </p>
        </div>

        <!-- SELECTION STATE: Period picker and confirm -->
        <div v-if="paymentState === 'selection' && status?.value !== 2" class="tw:py-2">
            <!-- No periods edge case -->
            <b-alert v-if="renewal_periods.length === 0" variant="warning" show>
                {{ $t('perfil_sga.sin_periodos') }}
            </b-alert>

            <!-- Period card -->
            <div v-if="selectedPeriod" class="tw:flex tw:items-center tw:p-5 tw:rounded-xl tw:text-white tw:mb-6 tw:shadow-lg tw:bg-gradient-to-br tw:from-indigo-500 tw:to-purple-600">
                <div class="tw:mr-4 tw:opacity-90">
                    <b-icon icon="calendar-check" font-scale="2" />
                </div>
                <div class="period-info">
                    <h4 class="tw:text-xl tw:font-bold tw:mb-1">{{ selectedPeriod.name }}</h4>
                    <p class="tw:text-sm tw:opacity-90 tw:mb-0">
                        <b-icon icon="calendar3" class="tw:mr-1" />
                        {{ formatDate(selectedPeriod.start_date) }} — {{ formatDate(selectedPeriod.end_date) }}
                    </p>
                </div>
            </div>

            <!-- Period selector (only if multiple) -->
            <b-form-group
                v-if="renewal_periods.length > 1"
                :label="$t('perfil_sga.seleccionar_periodo')"
                label-for="period-select"
                class="tw:mt-3"
            >
                <b-form-select
                    id="period-select"
                    v-model="selectedPeriodId"
                    :options="periodOptions"
                    :disabled="loading"
                />
            </b-form-group>

            <!-- Price breakdown -->
            <div v-if="selectedPeriod" class="tw:bg-gray-100 tw:rounded-xl tw:p-5 tw:mb-6">
                <div class="tw:flex tw:justify-between tw:items-center tw:py-2 tw:border-b tw:border-dashed tw:border-gray-300">
                    <span class="tw:text-sm tw:text-gray-500">{{ $t('perfil_sga.precio_renovacion') }}</span>
                    <span class="tw:text-base tw:font-semibold tw:text-gray-800" :class="{ 'tw:line-through tw:text-gray-400 tw:text-sm': hasDiscount }">
                        {{ formattedPrice }}
                    </span>
                </div>

                <!-- Individual promotion discounts -->
                <div
                    v-for="item in selectedPromotionBreakdown"
                    :key="item.name"
                    class="tw:flex tw:justify-between tw:items-center tw:py-2 tw:border-b tw:border-dashed tw:border-gray-300 tw:pl-4"
                >
                    <span class="tw:text-sm tw:text-gray-600 tw:flex tw:items-center tw:capitalize">
                        <b-icon icon="tag" class="tw:mr-1" />
                        {{ item.name }}
                    </span>
                    <span class="tw:text-green-600 tw:font-bold">-{{ formatPrice(item.amount) }}</span>
                </div>

                <div class="tw:flex tw:justify-between tw:items-center tw:pt-3 tw:mt-1 tw:border-t-2 tw:border-gray-300">
                    <span class="tw:text-sm tw:text-gray-500">{{ $t('perfil_sga.total') }}</span>
                    <span class="tw:text-2xl tw:font-extrabold tw:text-indigo-500">{{ formattedDiscountedPrice }}</span>
                </div>
            </div>

            <!-- Promotions section -->
            <div v-if="availablePromotions.length > 0" class="tw:mt-6">
                <p class="tw:text-base tw:font-semibold tw:text-gray-600 tw:mb-4 tw:flex tw:items-center">
                    <b-icon icon="tag" class="tw:mr-2" />
                    {{ $t('perfil_sga.promociones_disponibles') }}
                </p>
                <div class="tw:flex tw:flex-col tw:gap-3">
                    <div
                        v-for="(promotion, index) in availablePromotions"
                        :key="index"
                        class="tw:flex tw:items-center tw:p-3.5 tw:bg-white tw:border-2 tw:border-gray-200 tw:rounded-lg tw:cursor-pointer tw:transition-all tw:duration-200 tw:hover:border-blue-500 tw:hover:translate-x-1 tw:hover:shadow-md"
                        :class="{ 'tw:border-green-500 tw:bg-green-50 tw:hover:border-green-500 tw:hover:shadow-green-100': isPromotionSelected(index) }"
                        @click="togglePromotion(index)"
                    >
                        <div class="tw:mr-3.5 tw:text-lg">
                            <b-icon v-if="isPromotionSelected(index)" icon="check-circle-fill" variant="success" />
                            <b-icon v-else icon="circle" variant="secondary" />
                        </div>
                        <div class="tw:flex tw:flex-col tw:flex-1">
                            <span class="tw:font-semibold tw:text-gray-800 tw:text-sm">{{ promotion.name }}</span>
                            <span class="tw:text-xs tw:text-green-600 tw:font-bold">
                                <span v-if="promotion.type.name === 'Percentage'">-{{ promotion.value }}%</span>
                                <span v-else>-{{ formatPrice(promotion.value) }}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Discounts section -->
            <div v-if="availableDiscounts.length > 0" class="tw:mt-6">
                <p class="tw:text-base tw:font-semibold tw:text-gray-600 tw:mb-4 tw:flex tw:items-center">
                    <b-icon icon="percent" class="tw:mr-2" />
                    {{ $t('perfil_sga.descuentos_disponibles') }}
                </p>
                <div class="tw:flex tw:flex-col tw:gap-3">
                    <div
                        v-for="(discount, index) in availableDiscounts"
                        :key="index"
                        class="tw:flex tw:items-center tw:p-3.5 tw:bg-white tw:border-2 tw:border-gray-200 tw:rounded-lg tw:cursor-pointer tw:transition-all tw:duration-200 tw:hover:border-blue-500 tw:hover:translate-x-1 tw:hover:shadow-md"
                        :class="{ 'tw:border-green-500 tw:bg-green-50 tw:hover:border-green-500 tw:hover:shadow-green-100': isDiscountSelected(index) }"
                        @click="toggleDiscount(index)"
                    >
                        <div class="tw:mr-3.5 tw:text-lg">
                            <b-icon v-if="isDiscountSelected(index)" icon="check-circle-fill" variant="success" />
                            <b-icon v-else icon="circle" variant="secondary" />
                        </div>
                        <div class="tw:flex tw:flex-col tw:flex-1">
                            <span class="tw:font-semibold tw:text-gray-800 tw:text-sm">{{ discount.name }}</span>
                            <span class="tw:text-xs tw:text-green-600 tw:font-bold">
                                <span v-if="discount.valueType === 'PERCENTAGE'">-{{ discount.value }}%</span>
                                <span v-else-if="discount.valueType === 'FIXED'">-{{ formatPrice(discount.value) }}</span>
                                <span v-else-if="typeof discount.percentage === 'number'">-{{ discount.percentage }}%</span>
                                <span v-else-if="discount.type === 'Percentage' || (discount.type && discount.type.name === 'Percentage')">-{{ discount.value }}%</span>
                                <span v-else>-{{ formatPrice(discount.value || 0) }}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Código de descuento (input + botón "Comprobar") -->
            <div class="tw:mt-4 tw:p-4 tw:bg-gray-50 tw:rounded-xl tw:border tw:border-gray-200">
                <label class="tw:block tw:text-sm tw:font-semibold tw:text-gray-700 tw:mb-2">
                    {{ $t('perfil_sga.cupon_descuento_label') }}
                </label>
                <div class="tw:flex tw:gap-2">
                    <b-form-input
                        v-model="codeInputValue"
                        :placeholder="$t('perfil_sga.cupon_descuento_label')"
                        :state="codeVerificationState === 'error' ? false : null"
                    />
                    <b-button
                        variant="primary"
                        :disabled="codeVerifyLoading"
                        @click="verifyCode"
                    >
                        {{ $t('perfil_sga.cupon_comprobar') }}
                    </b-button>
                </div>
                <b-alert
                    v-if="codeVerificationState === 'error' && codeErrorMessage"
                    variant="danger"
                    show
                    class="tw:mt-2"
                >
                    {{ codeErrorMessage }}
                </b-alert>
                <b-alert
                    v-if="codeVerificationState === 'success' && verifiedCodeDiscount"
                    variant="success"
                    show
                    class="tw:mt-2"
                >
                    {{ verifiedCodeDiscount.name }}
                </b-alert>
            </div>

        </div>

        <!-- SUCCESS STATE: Purchase completed -->
        <div v-if="paymentState === 'success'" class="tw:text-center tw:py-8 tw:px-4">
            <div class="tw:mb-6" style="animation: successPop 0.5s ease;">
                <b-icon icon="check-circle-fill" variant="success" font-scale="4" />
            </div>
            <h3 class="tw:text-2xl tw:font-bold tw:text-green-600 tw:mb-2">{{ $t('perfil_sga.renovacion_exitosa') }}</h3>
            <p class="tw:text-base tw:text-gray-500 tw:mb-8">{{ $t('perfil_sga.renovacion_gracias') }}</p>

            <div v-if="successData" class="tw:bg-gray-100 tw:rounded-xl tw:p-6 tw:mb-8 tw:text-left">
                <!-- Order number -->
                <div class="tw:flex tw:justify-between tw:py-3 tw:border-b tw:border-gray-200">
                    <span class="tw:text-sm tw:text-gray-500">{{ $t('gracias.n_pedido') }}</span>
                    <span class="tw:font-semibold tw:text-gray-800">{{ successData.tpv_order }}</span>
                </div>

                <!-- Abonado info (single) -->
                <div v-if="successData.abonados?.length > 0" class="tw:bg-white tw:rounded-lg tw:p-4 tw:my-4 tw:border tw:border-gray-200">
                    <div class="tw:flex tw:items-center tw:mb-2">
                        <b-icon icon="person" class="tw:text-2xl tw:text-indigo-500 tw:mr-3" />
                        <span class="tw:font-semibold tw:text-lg tw:text-gray-800">{{ successData.abonados[0].nombre }} {{ successData.abonados[0].apellidos || '' }}</span>
                    </div>
                    <p class="tw:text-sm tw:text-gray-500 tw:mb-0">
                        <b-icon icon="geo-alt" class="tw:mr-1" />
                        {{ successData.abonados[0].asiento?.zona }} — {{ $t('perfil_sga.fila') }} {{ successData.abonados[0].asiento?.fila }}, {{ $t('perfil_sga.asiento') }} {{ successData.abonados[0].asiento?.asiento }}
                    </p>
                </div>

                <!-- Price breakdown -->
                <div class="tw:mt-4 tw:pt-4 tw:border-t-2 tw:border-gray-300">
                    <div class="tw:flex tw:justify-between tw:py-3 tw:border-b tw:border-gray-200">
                        <span class="tw:text-sm tw:text-gray-500">{{ $t('perfil_sga.precio_renovacion') }}</span>
                        <span class="tw:font-semibold tw:text-gray-800" :class="{ 'tw:line-through tw:text-gray-400 tw:text-sm': hasDiscount }">{{ formatPrice(successData.importe_total_sin_descuento || successData.importe_total) }}</span>
                    </div>

                    <!-- Discounts -->
                    <div v-if="successData.descuentos?.length > 0" class="tw:py-2">
                        <div
                            v-for="(descuento, index) in successData.descuentos"
                            :key="index"
                            class="tw:flex tw:items-center tw:py-1 tw:text-sm tw:text-gray-600"
                        >
                            <b-icon icon="tag" class="tw:text-green-500 tw:mr-2 tw:text-xs" />
                            <span class="tw:flex-1">{{ descuento.name }}</span>
                            <span class="tw:font-semibold tw:text-green-600">-{{ formatPrice(descuento.amount) }}</span>
                        </div>
                    </div>

                    <!-- Price management charge -->
                    <div v-if="successData.price_management" class="tw:flex tw:justify-between tw:items-center tw:py-2 tw:text-sm">
                        <span class="tw:text-gray-600">
                            <b-icon icon="gear" class="tw:mr-1 tw:text-gray-400" />
                            {{ $t('perfil_sga.gastos_gestion') }}
                        </span>
                        <span class="tw:font-semibold tw:text-gray-800">+{{ formatPrice(successData.price_management) }}</span>
                    </div>

                    <div class="tw:flex tw:justify-between tw:py-3">
                        <span class="tw:text-sm tw:text-gray-500">{{ $t('perfil_sga.importe_total') }}</span>
                        <span class="tw:font-semibold tw:text-gray-800 tw:text-xl tw:text-indigo-500">{{ formatPrice(successData.importe_total) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- PAYMENT STATE: Show Paycomet form or processing message -->
        <div v-if="paymentState === 'payment'">
            <b-alert v-if="paymentError" variant="danger" show>
                {{ paymentError }}
            </b-alert>

            <!-- Paycomet: Show form + 3D Secure iframe if needed -->
            <div v-if="!paymentProcessing">
                <!-- Payment cart summary -->
                <div class="tw:bg-gray-100 tw:rounded-xl tw:p-5 tw:mb-6">
                    <p class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wider tw:mb-4">{{ $t('perfil_sga.resumen_pago') }}</p>
                    <div class="tw:flex tw:justify-between tw:items-center tw:py-1.5 tw:text-sm">
                        <span class="tw:text-gray-500">{{ $t('perfil_sga.precio_renovacion') }}</span>
                        <span class="tw:font-semibold tw:text-gray-800" :class="{ 'tw:line-through tw:text-gray-400 tw:text-sm': hasDiscount }">{{ formatPrice(selectedPeriod?.price || 0) }}</span>
                    </div>
                    <div
                        v-for="item in selectedPromotionBreakdown"
                        :key="item.name"
                        class="tw:flex tw:justify-between tw:items-center tw:py-1.5 tw:text-sm tw:text-green-600 tw:pl-2"
                    >
                        <span class="tw:text-gray-500 tw:capitalize">
                            <b-icon icon="tag" class="tw:mr-1" />
                            {{ item.name }}
                        </span>
                        <span class="tw:font-bold tw:text-green-600">-{{ formatPrice(item.amount) }}</span>
                    </div>
                    <div v-if="gastos_gestion > 0" class="tw:flex tw:justify-between tw:items-center tw:py-1.5 tw:text-sm tw:text-gray-500 tw:text-xs">
                        <span class="tw:text-gray-500">{{ $t('perfil_sga.gastos_gestion') }}</span>
                        <span class="tw:font-semibold tw:text-gray-800">+{{ formatPrice(gastos_gestion) }}</span>
                    </div>
                    <div class="tw:flex tw:justify-between tw:items-center tw:mt-3 tw:pt-3 tw:border-t-2 tw:border-gray-300">
                        <span class="tw:text-base tw:font-semibold tw:text-gray-800">{{ $t('perfil_sga.total_a_pagar') }}</span>
                        <span class="tw:text-xl tw:font-extrabold tw:text-indigo-500">{{ formattedTotalPrice }}</span>
                    </div>
                </div>

                <!-- Import FormularioPagoComponent with jet_id -->
                <FormularioPagoComponent 
                    :jet_id="paymentData.jet_id" 
                    @payToken="handlePayToken"
                />
            </div>

            <!-- 3D Secure challenge iframe -->
            <div v-else-if="paymentChallengeUrl">
                <p class="tw:text-center tw:mb-3">{{ $t('perfil_sga.procesando_pago') }}</p>
                <iframe 
                    class="tw:shadow tw:my-2 tw:bg-white" 
                    style="display: block; margin: 0 auto; overflow-y: scroll; overflow-x: hidden;" 
                    title="3D Secure"
                    sandbox="allow-top-navigation allow-scripts allow-same-origin allow-forms" 
                    :src="paymentChallengeUrl" 
                    width="325"
                    height="523" 
                    frameborder="0" 
                    scrolling="yes"
                    loading="lazy"
                ></iframe>
            </div>

            <!-- Payment processing spinner -->
            <div v-else class="tw:text-center">
                <b-spinner label="Procesando..."/>
                <p class="tw:mt-2">{{ $t('perfil_sga.procesando_pago') }}</p>
            </div>
        </div>

        <!-- Hidden div for Redsys form injection -->
        <div ref="redsysFormContainer" style="display: none;"></div>

        <!-- Modal Footer (footer slot) -->
        <template #footer>
            <!-- Blocked state: just close -->
            <template v-if="status?.value === 2">
                <b-button variant="primary" @click="closeModal">
                    {{ $t('perfil_sga.cerrar') }}
                </b-button>
            </template>

            <!-- Selection state: cancel or confirm -->
            <template v-else-if="paymentState === 'selection'">
                <b-button variant="secondary" @click="closeModal" :disabled="loading" class="tw:mr-2">
                    {{ $t('formulario_pago.cancelar') }}
                </b-button>
                <b-button
                    variant="primary"
                    @click="confirmRenovacion"
                    :disabled="!canConfirm"
                >
                    <b-spinner v-if="loading" small class="tw:mr-1" />
                    {{ $t('perfil_sga.confirmar_renovacion') }}
                </b-button>
            </template>

            <!-- Payment state: cancel deletes cart and closes modal -->
            <template v-if="paymentState === 'payment'">
                <b-button
                    variant="secondary"
                    @click="cancelPayment"
                    :disabled="paymentProcessing || loading"
                    class="tw:mr-2"
                >
                    <b-spinner v-if="loading" small class="tw:mr-1" />
                    {{ $t('formulario_pago.cancelar') }}
                </b-button>
            </template>

            <!-- Success state: close modal -->
            <template v-if="paymentState === 'success'">
                <b-button
                    variant="primary"
                    @click="closeModal"
                >
                    {{ $t('perfil_sga.cerrar') }}
                </b-button>
            </template>
        </template>
    </ModalComponent>
</template>

<script>
    import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from 'vue';
    import ModalComponent from '@/components/backend/components/ModalComponent';
    import FormularioPagoComponent from '@/components/frontend/compra/FormularioPagoComponent.vue';
    import useSgaRenovacionPrecios from '@/composables/front/sga/useSgaRenovacionPrecios';
    import useSgaRenovacionPago from '@/composables/front/sga/useSgaRenovacionPago';
    import useSgaRenovacionDescuentos from '@/composables/front/sga/useSgaRenovacionDescuentos';

    export default {
        name: 'RenovacionSgaModal',

        components: {
            ModalComponent,
            FormularioPagoComponent
        },

        props: {
            abonado_id: {
                type: [String, Number],
                required: true
            },
            renewal_periods: {
                type: Array,
                default: () => []
            },
            sales_category_id: {
                type: String,
                default: null
            },
            promotions: {
                type: Array,
                default: () => []
            },
            discounts: {
                type: Array,
                default: () => []
            },
            gastos_gestion: {
                type: Number,
                default: 0
            },
            status: {
                type: Object,
                default: null
            }
        },

        setup(props, { emit }) {
            const instance = getCurrentInstance();

            // Local refs shared between composables
            const selectedPeriodId = ref(null);
            const selectedPromotions = ref([]);

            // Computed period object
            const selectedPeriod = computed(() => {
                if (!selectedPeriodId.value) return null;
                return props.renewal_periods.find(p => p.id === selectedPeriodId.value);
            });

            // Price formatter function
            const formatPrice = price => new Intl.NumberFormat('es-ES', {
                style: 'currency',
                currency: 'EUR'
            }).format(price);

            // Display a discount as "20%" or "10,00 €" depending on its type.
            // Handles SGA shape ({ valueType, value }), legacy discount shape
            // ({ percentage }) and promotion shape ({ type: "Percentage"|"FixedValue", value }).
            const discountDisplay = discount => {
                if (!discount) return '';
                if (discount.valueType === 'PERCENTAGE' && typeof discount.value === 'number') {
                    return `${discount.value}%`;
                }
                if (discount.valueType === 'FIXED' && typeof discount.value === 'number') {
                    return formatPrice(discount.value);
                }
                if (typeof discount.percentage === 'number') {
                    return `${discount.percentage}%`;
                }
                if (discount.type === 'Percentage' || (discount.type && discount.type.name === 'Percentage')) {
                    return `${discount.value}%`;
                }
                return formatPrice(discount.value || 0);
            };

            // Discount composable
            const descuentos = useSgaRenovacionDescuentos({
                discounts: props.discounts,
                abonado_id: props.abonado_id,
                store: instance.proxy.$store,
                config: instance.proxy.config,
                toast: instance.proxy.$toast,
                t: instance.proxy.$t.bind(instance.proxy)
            });

            // Pricing composable
            const precios = useSgaRenovacionPrecios({
                selectedPeriod,
                selectedPromotions,
                promotions: props.promotions,
                formatPrice,
                selectedDiscounts: descuentos.selectedDiscounts,
                verifiedCodeDiscount: descuentos.verifiedCodeDiscount
            });

            // Context for payment composable
            const ctx = {
                $store: instance.proxy.$store,
                $router: instance.proxy.$router,
                $toast: instance.proxy.$toast,
                $t: instance.proxy.$t.bind(instance.proxy),
                $config: instance.proxy.config,
                $emit: emit
            };

            // Payment composable
            const pago = useSgaRenovacionPago(ctx, {
                selectedPeriod,
                selectedPromotions,
                allActiveDiscounts: descuentos.allActiveDiscounts,
                verifiedCodeDiscount: descuentos.verifiedCodeDiscount,
                abonado_id: props.abonado_id,
                sales_category_id: props.sales_category_id,
                gastos_gestion: props.gastos_gestion,
                refs: instance.proxy.$refs
            });

            // Period options computed
            const periodOptions = computed(() => {
                return props.renewal_periods.map(period => ({
                    value: period.id,
                    text: `${period.name} (${formatPrice(period.price)})`
                }));
            });

            // Formatted base price (reuses formatPrice helper)
            const formattedPrice = computed(() => {
                if (!selectedPeriod.value) return '';
                return formatPrice(selectedPeriod.value.price);
            });

            // Formatted total price for payment state (reuses formatPrice helper)
            const formattedTotalPrice = computed(() => {
                if (!pago.paymentData.value.total_price) return '';
                return formatPrice(pago.paymentData.value.total_price);
            });

            // Guard for confirm button
            const canConfirm = computed(() => {
                return (
                    !pago.loading.value &&
                    selectedPeriodId.value !== null &&
                    props.renewal_periods.length > 0
                );
            });

            // Close modal helper
            const closeModal = () => {
                descuentos.resetDiscounts();
                emit('close');
            };

            // Format date helper
            const formatDate = dateString => {
                const date = new Date(dateString);
                return date.toLocaleDateString('es-ES');
            };

            // Lifecycle: auto-select single period
            onMounted(() => {
                if (props.renewal_periods.length === 1) {
                    selectedPeriodId.value = props.renewal_periods[0].id;
                }
            });

            // Lifecycle: cleanup payment polling and discount state
            onUnmounted(() => {
                pago.stopPaymentCheck();
                descuentos.resetDiscounts();
            });

            return {
                // Selection state
                selectedPeriodId,
                selectedPeriod,
                selectedPromotions,
                periodOptions,
                formattedPrice,
                formattedTotalPrice,
                canConfirm,
                formatPrice,
                closeModal,
                formatDate,
                discountDisplay,

                // Pricing composable
                ...precios,

                // Discount composable
                ...descuentos,

                // Payment composable
                ...pago
            };
        }
    };
</script>

<style>
@keyframes successPop {
    0% { transform: scale(0); opacity: 0; }
    70% { transform: scale(1.2); }
    100% { transform: scale(1); opacity: 1; }
}
</style>
